<template>
  <Teleport to="body">
    <div
      class="fixed top-0 left-0 w-full modalHeight modalBg z-modalsContent flex items-center justify-center p-16"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="success ? 'reqOpp-success-title' : 'reqOpp-title'"
      :aria-describedby="'reqOpp-desc'"
      @click.self="background"
      @keydown.esc="close"
    >
      <div
        class="max-w-556 p-16 desk:p-32 max-h-[80vh] overflow-y-auto shadow-lg transition-none"
        :class="[ success ? 'bg-bg-success' : 'bg-bg-base' ]"
        :aria-busy="isSubmitting ? 'true' : 'false'"
      >
        <!-- Header -->
        <div class="flex items-center justify-between gap-8 mb-8">
          <div
            :id="success ? 'reqOpp-success-title' : 'reqOpp-title'"
            class="inline-flex items-center gap-8 type-headline-lg"
            :class="[ success ? 'text-text-success' : 'text-text-base' ]"
          >
            <GlobalIcon
              v-if="success"
              name="check-circle"
              size="medium"
            />
            {{ success ? $t('modal.requestOrderProposal.successTitle') : $t('modal.requestOrderProposal.title') }}
          </div>
          <button
            ref="closeBtn"
            type="button"
            @click="close"
          >
            <GlobalIcon name="close" size="small" />
          </button>
        </div>

        <!-- Live region -->
        <p
          class="sr-only"
          role="status"
          aria-live="polite"
        >
          <span v-if="isSubmitting">{{ $t('common.loading') }}</span>
          <span v-else-if="success">{{ $t('modal.requestOrderProposal.successAnnounce') }}</span>
        </p>

        <!-- Success -->
        <div v-if="success">
          <p id="reqOpp-desc" class="type-sm text-text-subtle mb-16">
            {{ $t('modal.requestOrderProposal.successMessage') }}
          </p>

          <div v-if="isSubmitting" class="flex items-center justify-center">
            <LoadingIcon />
          </div>

          <div v-else class="flex items-center justify-end gap-24">
            <button
              type="button"
              class="btn btn--text"
              @click="close"
            >
              {{ $t('modal.requestOrderProposal.successCancel') }}
            </button>
            <button
              type="button"
              class="btn"
              @click="goToOrderProposals"
            >
              {{ $t('modal.requestOrderProposal.successCta') }}
            </button>
          </div>
        </div>

        <!-- Form -->
        <div v-else>
          <p id="reqOpp-desc" class="type-sm text-text-subtle mb-16">
            {{ $t('modal.requestOrderProposal.paragraph') }}
          </p>

          <div v-if="isSubmitting" class="flex items-center justify-center">
            <LoadingIcon />
          </div>

          <form v-else @submit.prevent="confirm">
            <fieldset :disabled="isSubmitting">
              <legend class="type-headline-sm mb-8 inline-flex items-center gap-6">
                {{ $t('modal.requestOrderProposal.formTitle') }}
                <Tooltip :text="$t('modal.requestOrderProposal.formTooltip')">
                  <global-icon name="question-circle" size="xsmall" />
                </Tooltip>
              </legend>

              <ul class="flex flex-col gap-16">
                <li>
                  <TextAreaInput
                    v-model="note"
                    :label="$t('modal.requestOrderProposal.formPlaceholder')"
                    server-name="required"
                    type="text"
                  />
                </li>
                <li>
                  <div>
                    <h4 class="mb-12 type-headline-sm">{{ $t('modal.requestOrderProposal.summaryTitle') }}:</h4>
                    <div
                      v-for="item in items"
                      :key="item.partNo"
                      class="flex justify-between mb-4"
                    >
                      <div class="flex items-center">
                        <div class="type-sm text-text-subtle leading-single mr-8">{{ item.name }}</div>
                        <div class="type-sm text-text-subtle leading-single">({{ unitPrice(item) }} /{{ unitDisplay(item) }})</div>
                      </div>
                      <div class="type-headline-sm whitespace-nowrap">{{ item.priceInfoTotal.priceBeforeVatDisplay }}</div>
                    </div>
                    <div class="border-b border-t border-light py-12">
                      <div class="flex justify-between items-end mb-8 type-sm leading-single">
                        <div>{{ $t('minicart.summary.exVat') }}</div>
                        <div class="flex items-center gap-8">
                          <div
                            v-if="cartStore.cart.summary.priceBeforeVatAndDiscount && cartStore.cart.summary.priceBeforeVatAndDiscount !== cartStore.cart.summary.priceBeforeVat"
                            class="type-base text-text-subtle line-through"
                          >
                            {{ cartStore.cart.summary.priceBeforeVatAndDiscountDisplay }}
                          </div>
                          <div
                            class="leading-single type-headline-base"
                            :class="{
                              'text-text-errorStrong': cartStore.cart.summary.priceBeforeVatAndDiscount && cartStore.cart.summary.priceBeforeVatAndDiscount !== cartStore.cart.summary.priceBeforeVat,
                            }"
                          >
                            {{ cartStore.cart.summary.priceBeforeVatDisplay }}
                          </div>
                        </div>
                      </div>
                      <div class="flex justify-between items-end text-text-subtle">
                        <div class="capitalize type-sm">({{ $t('price.incVat.short') }})</div>
                        <div class="pl-16 text-right">({{ cartStore.cart.summary.priceDisplay.trim() }})</div>
                      </div>
                    </div>
                  </div>
                </li>

                <li class="flex items-center justify-end gap-24">
                  <button
                    type="button"
                    class="btn btn--text"
                    :disabled="isSubmitting"
                    @click="abort"
                  >
                    {{ $t('modal.requestOrderProposal.cancel') }}
                  </button>
                  <button
                    type="button"
                    class="btn"
                    :disabled="isSubmitting"
                    @click="confirm"
                  >
                    {{ $t('modal.requestOrderProposal.cta') }}
                  </button>
                </li>
              </ul>
            </fieldset>
          </form>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import Tooltip from '~/components/body/Tooltip.vue';
import TextAreaInput from '~/components/form-elements/TextAreaInput.vue';
import useLinkReplacer from '~/composeables/useLinkReplacer';
import { useCartStore } from '~/store/cart';
import { useGlobalContentStore } from '~/store/globalContent';
import { storeToRefs } from 'pinia';
import { useLocalizedCurrency } from '~/composeables/useLocalizedCurrency';
import usePackageAreaConverter from '~/composeables/usePackageAreaConverter';
import { CartApiItem } from '~/constants/types/norce';
const { accountUrl } = useLinkReplacer();
const cartStore = useCartStore();
const globalContent = useGlobalContentStore();
const { items } = storeToRefs(cartStore);

const goToOrderProposals = () => {
  navigateTo(accountUrl('orderProposals'));
  emit('close');
};

const { $t } = useNuxtApp();

const emit = defineEmits<{
  (e:'abort'): void
  (e:'close'): void
  (e:'clickOutside'): void
}>();

const success = ref(false);
const isSubmitting = ref(false);
const closeBtn = ref<HTMLButtonElement | null>(null);
const note = ref<string>($t('modal.requestOrderProposal.formPreFilled') as unknown as string);

const background = () => {
  if (success.value && !isSubmitting.value) {
    emit('close');
    emit('clickOutside');
  }
};

const confirm = async() => {
  isSubmitting.value = true;
  const ok = await cartStore.requestOrderProposal(
    note.value
  );
  if (ok) {
    success.value = true;
  }
  isSubmitting.value = false;
};

const abort = () => {
  emit('abort');
  emit('close');
};

const close = () => {
  success.value = false;
  isSubmitting.value = false;
  emit('close');
};

const unitPrice = (product: CartApiItem) => {
  const { convertPackagePriceToAreaPrice } = usePackageAreaConverter(ref(product));

  const priceBeforeDiscountBeforeVat = product.priceInfo.priceBeforeDiscountBeforeVat;
  const convertedPrice = convertPackagePriceToAreaPrice(priceBeforeDiscountBeforeVat);

  if (convertedPrice) {
    return useLocalizedCurrency(convertedPrice);
  }

  return globalContent.getDisplayIncVat ?
    product.priceInfo.priceBeforeDiscountDisplay :
    product.priceInfo.priceBeforeDiscountBeforeVatDisplay;
};

const unitDisplay = (product: CartApiItem) => {
  const { unitDisplay } = usePackageAreaConverter(ref(product));
  return unitDisplay;
};
</script>
